/**
 * S1 — essential connections (ONBOARDING-FLOW-SPEC.md S1), front-loaded
 * right after the S0 branch answer and before the rest of the interview
 * continues. Reuses the EXACT OAuth popup + postMessage completion flow
 * from pages/Connectors.tsx: the authenticated authorize call returns the
 * IdP URL (a plain redirect is frame-blocked embedded and can't carry the
 * Bearer token), the popup completes at the backend callback, which
 * postMessages {type:'oauth_result', ok} back to this window.
 *
 * The branch -> essential-connector map is spec config, not tree data:
 * S1 has no representation in the interview tree at all (it is a
 * front-loaded UI phase, not a scripted step), so it necessarily lives
 * here rather than being derived from anything the API returns.
 */

import { useEffect, useRef, useState } from 'react';
import { Badge, BlockStack, Button, InlineStack, Text } from '@shopify/polaris';

import { getAuthorizeUrl, type ConnectorSummary } from '../../services/connectorsApi';

interface Ask {
  label: string;
  /** OR-set: any one of these connectors satisfies the ask. */
  connectorKeys: string[];
  required: boolean;
  skipCost?: string;
}

const BRANCH_ASKS: Record<string, Ask[]> = {
  shopify_merchant: [
    { label: 'Shopify', connectorKeys: ['shopify'], required: true },
    {
      label: 'An ad platform',
      connectorKeys: ['meta_ads', 'google_ads'],
      required: false,
      skipCost:
        'Without an ad platform connected, the Facebook Ads and Google Ads Agents stay asleep. Connect later from Connections.',
    },
    {
      label: 'Klaviyo',
      connectorKeys: ['klaviyo'],
      required: false,
      skipCost:
        'Without Klaviyo, email and flow performance stay out of your digests. Connect later from Connections.',
    },
  ],
  b2b_saas: [
    { label: 'HubSpot', connectorKeys: ['hubspot'], required: true },
    {
      label: 'An ad platform',
      connectorKeys: ['linkedin_ads', 'google_ads'],
      required: false,
      skipCost:
        'Without an ad platform connected, the LinkedIn Ads and Google Ads Agents stay asleep. Connect later from Connections.',
    },
    {
      label: 'Google Analytics 4',
      connectorKeys: ['ga4'],
      required: false,
      skipCost:
        'Without GA4, organic traffic stays out of your digests. Connect later from Connections.',
    },
  ],
};

const CONNECTOR_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  meta_ads: 'Meta Ads',
  google_ads: 'Google Ads',
  linkedin_ads: 'LinkedIn Ads',
  hubspot: 'HubSpot',
  klaviyo: 'Klaviyo',
  ga4: 'Google Analytics 4',
};

interface ConnectStepProps {
  /** The S0 vertical answer (session.branch). */
  branch: string;
  connectors: ConnectorSummary[];
  /** A connect completed — refetch connectors and refresh the right panel. */
  onConnected: () => void;
  onContinue: () => void;
}

export function ConnectStep({
  branch,
  connectors,
  onConnected,
  onContinue,
}: ConnectStepProps) {
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'oauth_result') {
        if (event.data.ok) {
          onConnected();
        } else {
          setError(`Connection failed (${event.data.detail || 'unknown'})`);
        }
        popupRef.current = null;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onConnected]);

  const asks = BRANCH_ASKS[branch];

  // Agency: each client's own sources connect from that client's own
  // workspace (the spec's agency loop, out of this page's scope) — there
  // is nothing to ask at the agency's own top level.
  if (!asks) {
    return (
      <BlockStack gap="200">
        <Text as="p" variant="bodyMd">
          You will connect each client&apos;s sources from that
          client&apos;s workspace once it is created.
        </Text>
        <InlineStack>
          <Button variant="primary" onClick={onContinue}>
            Continue
          </Button>
        </InlineStack>
      </BlockStack>
    );
  }

  const connectedKeys = new Set(
    connectors.filter((c) => c.is_connected).map((c) => c.connector_key)
  );
  const summaryByKey = new Map(
    connectors.map((c) => [c.connector_key, c] as const)
  );
  const askSatisfied = (ask: Ask) =>
    ask.connectorKeys.some((key) => connectedKeys.has(key));
  const canContinue = asks.filter((a) => a.required).every(askSatisfied);
  // Any unsatisfied ask whose connectors can't OAuth from here (Shopify's
  // token flow, Klaviyo's API key, or an OAuth provider this deploy has no
  // credentials for) completes on the Connections page — give those a
  // re-check path back on this one.
  const anyOffPageConnect = asks.some(
    (ask) =>
      !askSatisfied(ask) &&
      ask.connectorKeys.some(
        (key) => !(summaryByKey.get(key)?.oauth_available ?? false)
      )
  );

  const connect = async (connectorKey: string) => {
    setBusyKey(connectorKey);
    setError(null);
    try {
      const url = await getAuthorizeUrl(connectorKey);
      popupRef.current = window.open(
        url,
        'oauth_connect',
        'width=600,height=750,menubar=no,toolbar=no'
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start authorization');
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <BlockStack gap="300">
      <Text as="p" variant="bodyMd">
        Let&apos;s connect your essential sources.
      </Text>
      {error && (
        <Text as="p" tone="critical">
          {error}
        </Text>
      )}
      {asks.map((ask) => {
        const satisfied = askSatisfied(ask);
        return (
          <BlockStack gap="100" key={ask.label}>
            <InlineStack gap="200" blockAlign="center" wrap>
              {satisfied ? (
                <Badge tone="success">{`${ask.label} connected`}</Badge>
              ) : (
                <>
                  {ask.connectorKeys.map((key) =>
                    summaryByKey.get(key)?.oauth_available ? (
                      <Button
                        key={key}
                        loading={busyKey === key}
                        onClick={() => void connect(key)}
                      >
                        {`Connect ${CONNECTOR_LABELS[key] ?? key}`}
                      </Button>
                    ) : (
                      // No OAuth from this popup flow — the Connectors
                      // page carries this connector's real path (token /
                      // API-key modal). A dead authorize call here would
                      // error out and strand a required ask.
                      <Button
                        key={key}
                        onClick={() =>
                          window.open('/connectors', '_blank', 'noopener')
                        }
                      >
                        {`Connect ${
                          CONNECTOR_LABELS[key] ?? key
                        } via Connectors`}
                      </Button>
                    )
                  )}
                  {ask.required && <Badge tone="attention">Required</Badge>}
                </>
              )}
            </InlineStack>
            {!ask.required && !satisfied && ask.skipCost && (
              <Text as="p" tone="subdued" variant="bodySm">
                {ask.skipCost}
              </Text>
            )}
          </BlockStack>
        );
      })}
      <InlineStack gap="200">
        <Button variant="primary" disabled={!canContinue} onClick={onContinue}>
          Continue
        </Button>
        {anyOffPageConnect && (
          <Button variant="tertiary" onClick={onConnected}>
            I connected on the Connections page — check again
          </Button>
        )}
      </InlineStack>
    </BlockStack>
  );
}
